import {
  HHAlert,
  HHFormControlLabel,
  HHStack,
  HHSwitch,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import LinkIcon from '@mui/icons-material/Link';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { StatusBadge } from '../components/badges/status-badge';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { ContractInput } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { dedupePractitioners } from '../utils/dedupe-practitioners';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

interface PractitionersStepInnerProps {
  input: ContractInput;
}

const PractitionersStepInner = ({
  input,
}: PractitionersStepInnerProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const fired = useRef(false);

  useStepHeader({
    description:
      'One row per unique practitioner. Same-NPI rows across multiple locations collapse to a single ProviderIndividual; null-NPI rows are skipped.',
  });

  const deduped = useMemo(
    () => dedupePractitioners(input.locations),
    [input.locations],
  );

  const resolvable = useMemo(
    () => deduped.filter(d => d.npi != null),
    [deduped],
  );

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async (): Promise<void> => {
      for (const p of resolvable) {
        const npi = p.npi as string;
        if (state.resolutions.providerIndividuals[npi] != null) continue;
        try {
          const result = await client.resolveProviderIndividual({ npi1: npi });
          setState(s => ({
            ...s,
            resolutions: {
              ...s.resolutions,
              providerIndividuals: {
                ...s.resolutions.providerIndividuals,
                [npi]: result.ok
                  ? {
                      status: 'existing',
                      data: {
                        providerIndividualId:
                          result.data.providerIndividual.id ?? '',
                        name:
                          [
                            result.data.providerIndividual.givenName,
                            result.data.providerIndividual.surname,
                          ]
                            .filter(Boolean)
                            .join(' ') || p.name,
                      },
                    }
                  : { status: 'new', reason: result.reason },
              },
            },
          }));
        } catch (e) {
          setState(s => ({
            ...s,
            resolutions: {
              ...s.resolutions,
              providerIndividuals: {
                ...s.resolutions.providerIndividuals,
                [npi]: {
                  status: 'conflict',
                  conflictFields: [
                    {
                      field: 'network',
                      incoming: npi,
                      existing: (e as Error).message,
                    },
                  ],
                },
              },
            },
          }));
        }
      }
    })();
  }, [client, resolvable, setState, state.resolutions.providerIndividuals]);

  const setCreateAccessPoints = (next: boolean): void => {
    setState(s => ({
      ...s,
      decisions: { ...s.decisions, createAccessPoints: next },
    }));
  };

  const allResolved = resolvable.every(d => {
    const r = state.resolutions.providerIndividuals[d.npi as string];
    return r != null && (r.status === 'existing' || r.status === 'new');
  });
  const disabledInfo: DisabledInfo | undefined = allResolved
    ? undefined
    : {
        message:
          'Some practitioners are still resolving or have conflicts. Wait for all to settle before continuing.',
        severity: 'warning',
      };

  const skippedCount = deduped.filter(d => d.npi == null).length;
  const crossLocationCount = deduped.filter(d => d.instances.length > 1).length;

  return (
    <HHStack spacing={3}>
      <HHStack
        direction="row"
        spacing={4}
        alignItems="center"
        sx={{
          px: 2,
          py: 1,
          backgroundColor: 'rgba(0,0,0,0.03)',
          borderRadius: 1,
        }}
      >
        <HHFormControlLabel
          label="Create AccessPoints for per-practitioner pricing"
          control={
            <HHSwitch
              hhVariant="variant-bypass"
              checked={state.decisions.createAccessPoints}
              onChange={(_, v): void => setCreateAccessPoints(v)}
            />
          }
        />
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7, maxWidth: 480 }}>
          For case-rate contracts, MerchantLocation-level pricing is usually
          enough. Turn on only if rates differ by individual practitioner at the
          same clinic.
        </HHTypography>
      </HHStack>

      {(skippedCount > 0 || crossLocationCount > 0) && (
        <HHAlert hhVariant="outlined" severity="info">
          <HHStack spacing={0.5}>
            {crossLocationCount > 0 && (
              <HHTypography hhVariant="body2">
                <strong>{crossLocationCount}</strong>{' '}
                {crossLocationCount === 1
                  ? 'practitioner appears'
                  : 'practitioners appear'}{' '}
                at multiple locations — each will resolve to one
                ProviderIndividual.
              </HHTypography>
            )}
            {skippedCount > 0 && (
              <HHTypography hhVariant="body2">
                <strong>{skippedCount}</strong> row
                {skippedCount === 1 ? '' : 's'} with no NPI will be skipped.
                Repricing for those claims falls through to
                MerchantLocation-level CSPs.
              </HHTypography>
            )}
          </HHStack>
        </HHAlert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Name</TableCell>
              <TableCell sx={{ width: 140 }}>NPI</TableCell>
              <TableCell>Locations</TableCell>
              <TableCell sx={{ width: 130 }}>Status</TableCell>
              <TableCell sx={{ width: 220 }}>Provider ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deduped.map(d => {
              if (d.npi == null) {
                return (
                  <TableRow
                    key={d.identityKey}
                    sx={{ opacity: 0.5, textDecoration: 'line-through' }}
                  >
                    <TableCell />
                    <TableCell>
                      <HHTooltip
                        hhVariant="variant-bypass"
                        placement="top"
                        title="No NPI — cannot resolve in KG. Repricing falls back to MerchantLocation-level CSPs."
                      >
                        <span>{d.name}</span>
                      </HHTooltip>
                    </TableCell>
                    <TableCell sx={monoText}>—</TableCell>
                    <TableCell>{d.instances[0].locationCity}</TableCell>
                    <TableCell colSpan={2}>
                      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                        Skipped
                      </HHTypography>
                    </TableCell>
                  </TableRow>
                );
              }
              const r = state.resolutions.providerIndividuals[d.npi];
              const status = r?.status ?? 'pending';
              const cross = d.instances.length > 1;
              return (
                <TableRow key={d.identityKey}>
                  <TableCell>
                    {cross && (
                      <HHTooltip
                        hhVariant="variant-bypass"
                        placement="top"
                        title={`Same individual across ${d.instances.length} locations`}
                      >
                        <LinkIcon fontSize="small" sx={{ color: '#0d6efd' }} />
                      </HHTooltip>
                    )}
                  </TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell sx={monoText}>{d.npi}</TableCell>
                  <TableCell>
                    {d.instances.map(i => i.locationCity).join(', ')}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={status} />
                  </TableCell>
                  <TableCell sx={monoText}>
                    {r?.data?.providerIndividualId ?? '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <StepNav
        id="practitioners"
        canContinue={allResolved}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const PractitionersStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  return <PractitionersStepInner input={state.input} />;
};
